import React, { useState, useEffect, useRef } from "react";
import ArrowIcon from '../../images/arrow_button_gurugaman.svg'
import "./BMTCPrivacy.css";

const AccordionSection = ({
  title,
  children,
  isOpen,
  onToggle,
  sectionRef,
}) => {
  return (
    <div
      className={`bmtc-accordion-section ${isOpen ? "open" : ""}`}
      ref={sectionRef}
    >
      <button
        className="bmtc-accordion-header"
        onClick={onToggle}
        aria-expanded={isOpen}
      >
        <span>{title}</span>
        <div className="bmtc-accordion-icon-wrapper">
          <img src={ArrowIcon} alt="Arrow Icon" className={`bmtc-accordion-icon ${isOpen ? "open" : ""}`} />
        </div>
      </button>
      {isOpen && <div className="bmtc-accordion-content">{children}</div>}
    </div>
  );
};

const BMTCPrivacy = () => {
  const [showSkipButton, setShowSkipButton] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [openSections, setOpenSections] = useState([]);

  const personalRef = useRef(null);
  const servicesRef = useRef(null);
  const rightsRef = useRef(null);

  const handleLinkClick = (section) => {
    setOpenSections((prev) =>
      prev.includes(section) ? prev : [...prev, section]
    );
    setTimeout(() => {
      if (section === "personal" && personalRef.current) {
        personalRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      } else if (section === "services" && servicesRef.current) {
        servicesRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      } else if (section === "rights" && rightsRef.current) {
        rightsRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 100);
  };

  const configureConsentUI = (shouldShowSkip) => {
    try {
      setShowSkipButton(shouldShowSkip);
      return "Configuration applied successfully";
    } catch (error) {
      console.error("Error applying configuration:", error);
      return "Error applying configuration";
    }
  };

  const toggleSkipButton = (show) => setShowSkipButton(show);

  const notifyPlatform = (eventType) => {
    const payload = { message: `privacy_${eventType}` };
    const message = {
      action: eventType,
      data: {
        consent:
          eventType === "accept"
            ? true
            : eventType === "deny"
            ? false
            : "skipped",
        timestamp: new Date().toISOString(),
      },
      timestamp: new Date().toISOString(),
    };

    try {
      if (window.webkit?.messageHandlers?.nativeApp) {
        window.webkit.messageHandlers.nativeApp.postMessage(payload);
      }
      if (window.webkit?.messageHandlers?.consentHandler) {
        window.webkit.messageHandlers.consentHandler.postMessage(message);
      }
      if (eventType === "accept" && window.webkit?.messageHandlers?.acceptClicked) {
        window.webkit.messageHandlers.acceptClicked.postMessage(null);
      }
      if (eventType === "deny" && window.webkit?.messageHandlers?.denyClicked) {
        window.webkit.messageHandlers.denyClicked.postMessage(message);
      }
      if (eventType === "skip" && window.webkit?.messageHandlers?.skipClicked) {
        window.webkit.messageHandlers.skipClicked.postMessage(message);
      }

      if (typeof window.AndroidInterface !== "undefined") {
        if (eventType === "accept") window.AndroidInterface.acceptClicked();
        else if (eventType === "deny") window.AndroidInterface.denyClicked();
        else if (eventType === "skip") window.AndroidInterface.skipClicked();
      }

      if (typeof window.Android !== "undefined" && window.Android.onConsentAction) {
        window.Android.onConsentAction(JSON.stringify(message));
      } else if (typeof window.postMessage === "function") {
        window.postMessage(payload, "*");
      }

      if (window.parent && window.parent !== window) {
        window.parent.postMessage(message, "*");
      }
    } catch (error) {
      console.error(`Error during ${eventType} click`, error);
      alert(`Privacy policy ${eventType}`);
    }

    console.log("Privacy Consent Action:", message);
  };

  const handleAccept = () => {
    setShowLoader(true);
    notifyPlatform("accept");
  };
  const handleDeny = () => {
    setShowLoader(true);
    notifyPlatform("deny");
  };
  const handleSkip = () => {
    setShowLoader(true);
    notifyPlatform("skip");
  };

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.hideConsentLoader = () => setShowLoader(false);
      window.showConsentLoader = () => setShowLoader(true);
      window.configureConsentUI = configureConsentUI;
      window.toggleSkipButton = toggleSkipButton;
    }
    return () => {
      if (typeof window !== "undefined") {
        delete window.hideConsentLoader;
        delete window.showConsentLoader;
        delete window.configureConsentUI;
        delete window.toggleSkipButton;
      }
    };
  }, []);

  const toggleSection = (section) => {
    setOpenSections((prev) =>
      prev.includes(section)
        ? prev.filter((s) => s !== section)
        : [...prev, section]
    );
  };

  return (
    <div className="privacy-container">
      <div className="bmtc-privacy-modal-container">
        <h1 className="bmtc-privacy-heading">
          We Value Your Privacy – Please Review & Accept
        </h1>
        <p className="bmtc-privacy-desc">
          At BMTC, we need to collect your{" "}
          <span
            className="bmtc-inline-link"
            onClick={() => handleLinkClick("personal")}
          >
            personal data
          </span>{" "}
          to provide{" "}
          <span
            className="bmtc-inline-link"
            onClick={() => handleLinkClick("services")}
          >
            our services
          </span>
          , and you have the{" "}
          <span
            className="bmtc-inline-link"
            onClick={() => handleLinkClick("rights")}
          >
            right
          </span>{" "}
          to manage it.
          <br />
          <br />
          Your privacy matters to us, so please take a moment to check our
          updated{" "}
          <a
            className="bmtc-hyperlink-text"
            href="https://chalo.com/terms-and-conditions/bmtc-terms-and-conditions"
            rel="noopener noreferrer"
          >
            Privacy Policy
          </a>
          . Without consent, access to the app will not be possible.
        </p>

        <div className="bmtc-accordion-group">
          <AccordionSection
            title="Personal data examples"
            sectionRef={personalRef}
            isOpen={openSections.includes("personal")}
            onToggle={() => toggleSection("personal")}
          >
            <div className="bmtc-privacy-info-box">
              <ul>
                <li>
                  <span className="bmtc-privacy-info-title">Personal info:</span>{" "}
                  <span className="bmtc-privacy-info-desc">
                    Name, phone number, gender, age, Aadhaar / student ID / disability certificate (as applicable).
                  </span>
                </li>
                <li>
                  <span className="bmtc-privacy-info-title">App & travel:</span>{" "}
                  <span className="bmtc-privacy-info-desc">
                    Pass ID, Mobile Wallet, routes, stops, transaction and search history.
                  </span>
                </li>
                <li>
                  <span className="bmtc-privacy-info-title">Financial:</span>{" "}
                  <span className="bmtc-privacy-info-desc">
                    Payment details, wallet balance.
                  </span>
                </li>
                <li>
                  <span className="bmtc-privacy-info-title">Location & device:</span>{" "}
                  <span className="bmtc-privacy-info-desc">
                    Coordinates, device ID, carrier, WiFi, screen size, app version.
                  </span>
                </li>
                <li>
                  <span className="bmtc-privacy-info-title">System:</span>{" "}
                  <span className="bmtc-privacy-info-desc">
                    Language preference, feature usage and performance analytics.
                  </span>
                </li>
              </ul>
            </div>
          </AccordionSection>

          <AccordionSection
            title="Our services"
            sectionRef={servicesRef}
            isOpen={openSections.includes("services")}
            onToggle={() => toggleSection("services")}
          >
            <p className="bmtc-privacy-desc">
              Ticketing, live tracking, enhanced user experience, Namma BMTC / third-party product / services' promotions, security, compliance, research and analytics. The app is operated by BMTC and powered by its technology partner, Chalo.
            </p>
          </AccordionSection>

          <AccordionSection
            title="Your rights"
            sectionRef={rightsRef}
            isOpen={openSections.includes("rights")}
            onToggle={() => toggleSection("rights")}
          >
            <p className="bmtc-privacy-desc">
              You can access, update, correct or delete your data, or withdraw consent any time. For assistance, contact us at complaints@mybmtc.com.
            </p>
          </AccordionSection>
        </div>

        <div className="bmtc-privacy-btn-group">
          <div className="bmtc-privacy-consent">
            I am above 18 years, and have read, understood, and consent to Namma BMTC's privacy policy.
            My child is below 18 years, and I as the parent have read, understood, and consented to Namma BMTC's privacy policy.
          </div>
          <button className="bmtc-privacy-btn accept" onClick={handleAccept}>
            Accept
          </button>
          <button className="bmtc-privacy-btn deny" onClick={handleDeny}>
            Deny
          </button>
          <button
            className={`bmtc-privacy-btn skip ${showSkipButton ? "" : "bmtc-hidden"}`}
            onClick={handleSkip}
          >
            I will do this later
          </button>
        </div>
      </div>

      {showLoader && (
        <div className="bmtc-fullscreen-loader">
          <div className="bmtc-loader-container">
            <div className="bmtc-loader-spinner"></div>
            <div className="bmtc-loader-text">Loading...</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BMTCPrivacy;
